import { useState, useEffect, useRef } from 'react';
import { Plus, Package, Trash2, Check, Truck, Wrench, X, Search, ChevronDown } from '../../icons/wp-icons';
import { getMaterialien, addMaterial, updateMaterial, deleteMaterial } from '../../api/projektApi';
import { katalogApi } from '../../api/katalogApi';
import toast from 'react-hot-toast';

export default function ProjektMaterialien({ projektId, onDataChange }) {
  const [materialien, setMaterialien] = useState([]);
  const [katalog, setKatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [formData, setFormData] = useState({
    katalogPositionId: '',
    bezeichnung: '',
    beschreibung: '',
    menge: 1,
    einheit: 'Stück',
    einzelpreisNetto: 0
  });

  // Katalog-Suche State
  const [katalogSuche, setKatalogSuche] = useState('');
  const [showKatalogDropdown, setShowKatalogDropdown] = useState(false);
  const [selectedKatalog, setSelectedKatalog] = useState(null);
  const katalogDropdownRef = useRef(null);

  useEffect(() => {
    loadData();
  }, [projektId]);

  // Schließe Dropdown wenn außerhalb geklickt wird
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (katalogDropdownRef.current && !katalogDropdownRef.current.contains(event.target)) {
        setShowKatalogDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadData = async () => {
    try {
      const [matData, katData] = await Promise.all([
        getMaterialien(projektId),
        katalogApi.getPositionen({ aktiv: true }).catch(() => [])
      ]);
      setMaterialien(matData);
      setKatalog(katData || []);
    } catch (error) {
      toast.error('Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({ katalogPositionId: '', bezeichnung: '', beschreibung: '', menge: 1, einheit: 'Stück', einzelpreisNetto: 0 });
    setSelectedKatalog(null);
    setKatalogSuche('');
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    try {
      await addMaterial(projektId, formData);
      toast.success('Material hinzugefügt');
      setShowAdd(false);
      resetForm();
      loadData();
      if (onDataChange) onDataChange();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Fehler beim Hinzufügen');
    }
  };

  const handleStatusChange = async (material, field) => {
    try {
      await updateMaterial(projektId, material.id, { [field]: !material[field] });
      loadData();
      if (onDataChange) onDataChange();
    } catch (error) {
      toast.error('Fehler beim Aktualisieren');
    }
  };

  const handleDelete = async (materialId) => {
    if (!confirm('Material wirklich entfernen?')) return;
    try {
      await deleteMaterial(projektId, materialId);
      toast.success('Material entfernt');
      loadData();
      if (onDataChange) onDataChange();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  // Gefilterte Katalog-Liste basierend auf Suche
  const gefilterterKatalog = katalog.filter(kat => {
    const suchText = katalogSuche.toLowerCase();
    return (
      kat.bezeichnung?.toLowerCase().includes(suchText) ||
      kat.artikelnummer?.toLowerCase().includes(suchText) ||
      kat.kategorie?.toLowerCase().includes(suchText)
    );
  });

  // Katalog-Position auswählen
  const handleKatalogSelect = (kat) => {
    setSelectedKatalog(kat);
    setFormData({
      ...formData,
      katalogPositionId: kat.id,
      bezeichnung: kat.bezeichnung,
      einheit: kat.einheit || 'Stück',
      einzelpreisNetto: Math.round((kat.verkaufspreis || 0) * 100)
    });
    setKatalogSuche('');
    setShowKatalogDropdown(false);
  };

  // Katalog-Auswahl entfernen (für freie Eingabe)
  const handleKatalogRemove = () => {
    setSelectedKatalog(null);
    setFormData({
      ...formData,
      katalogPositionId: '',
      bezeichnung: '',
      einzelpreisNetto: 0
    });
    setKatalogSuche('');
  };

  const formatCurrency = (cents) => {
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(cents / 100);
  };

  if (loading) {
    return (
      <div className="bg-surface rounded-card shadow-card p-6">
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  const gesamtNetto = materialien.reduce((sum, m) => sum + m.gesamtpreisNetto, 0);
  const gesamtKosten = materialien.reduce((sum, m) => sum + (m.gesamtkostenNetto || 0), 0);

  return (
    <div className="bg-surface rounded-card shadow-card p-6">
      <div className="flex justify-between items-center mb-4">
        <div>
          <h3 className="font-semibold text-text">Materialien ({materialien.length})</h3>
          <p className="text-sm text-text-muted">
            VK: {formatCurrency(gesamtNetto)}
            {gesamtKosten > 0 && (
              <span className="ml-3">EK: {formatCurrency(gesamtKosten)}</span>
            )}
            {gesamtKosten > 0 && (
              <span className="ml-3 text-green-600">Marge: {formatCurrency(gesamtNetto - gesamtKosten)}</span>
            )}
          </p>
        </div>
        <button
          onClick={() => setShowAdd(true)}
          className="flex items-center gap-2 px-3 py-2 bg-primary text-white rounded-button hover:bg-primary-dark transition text-sm"
        >
          <Plus className="w-4 h-4" />
          Material hinzufügen
        </button>
      </div>

      {/* Add Form */}
      {showAdd && (
        <div className="bg-surface-elevated rounded-card p-4 mb-4">
          <div className="flex justify-between items-center mb-4">
            <h4 className="font-medium text-text">Neues Material</h4>
            <button onClick={() => { setShowAdd(false); resetForm(); }} className="p-1 hover:bg-surface rounded">
              <X className="w-4 h-4" />
            </button>
          </div>
          <form onSubmit={handleAdd} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Durchsuchbares Katalog-Dropdown */}
              <div>
                <label className="block text-sm font-medium mb-1 text-text">Aus Katalog</label>
                <div className="relative" ref={katalogDropdownRef}>
                  {/* Ausgewählte Katalog-Position oder Suchfeld */}
                  {selectedKatalog ? (
                    <div className="flex items-center justify-between w-full px-3 py-2 border border-border rounded-button bg-gray-50">
                      <div className="min-w-0 flex-1">
                        <span className="font-medium text-sm block truncate">{selectedKatalog.bezeichnung}</span>
                        <span className="text-text-muted text-xs block truncate">
                          VK: {formatCurrency(Math.round((selectedKatalog.verkaufspreis || 0) * 100))}
                          {selectedKatalog.einkaufspreis != null && ` | EK: ${formatCurrency(Math.round(selectedKatalog.einkaufspreis * 100))}`}
                          {selectedKatalog.artikelnummer && ` | ${selectedKatalog.artikelnummer}`}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={handleKatalogRemove}
                        className="p-1.5 hover:bg-gray-200 rounded transition ml-2 shrink-0"
                        title="Für freie Eingabe entfernen"
                      >
                        <X className="w-4 h-4 text-gray-500" />
                      </button>
                    </div>
                  ) : (
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        value={katalogSuche}
                        onChange={(e) => {
                          setKatalogSuche(e.target.value);
                          setShowKatalogDropdown(true);
                        }}
                        onFocus={() => setShowKatalogDropdown(true)}
                        placeholder="Katalog durchsuchen oder freie Eingabe..."
                        className="w-full pl-10 pr-10 py-2 border border-border rounded-button focus:outline-none focus:ring-2 focus:ring-primary bg-surface text-text"
                      />
                      <button
                        type="button"
                        onClick={() => setShowKatalogDropdown(!showKatalogDropdown)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2"
                      >
                        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${showKatalogDropdown ? 'rotate-180' : ''}`} />
                      </button>
                    </div>
                  )}

                  {/* Dropdown Liste */}
                  {showKatalogDropdown && !selectedKatalog && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-border rounded-button shadow-lg max-h-60 overflow-y-auto">
                      {katalog.length === 0 ? (
                        <div className="px-4 py-3 text-text-muted text-center">
                          Kein Katalog vorhanden - nutzen Sie die freie Eingabe
                        </div>
                      ) : gefilterterKatalog.length === 0 ? (
                        <div className="px-4 py-3 text-text-muted text-center">
                          Keine Treffer - nutzen Sie die freie Eingabe unten
                        </div>
                      ) : (
                        gefilterterKatalog.slice(0, 50).map(kat => (
                          <button
                            key={kat.id}
                            type="button"
                            onClick={() => handleKatalogSelect(kat)}
                            className="w-full px-4 py-3 text-left hover:bg-gray-50 border-b border-border last:border-b-0 transition"
                          >
                            <div className="font-medium text-text">{kat.bezeichnung}</div>
                            <div className="text-sm text-text-muted flex gap-3 flex-wrap">
                              <span>VK: {formatCurrency(Math.round((kat.verkaufspreis || 0) * 100))}</span>
                              {kat.einkaufspreis != null && <span>EK: {formatCurrency(Math.round(kat.einkaufspreis * 100))}</span>}
                              {kat.artikelnummer && <span>Art.-Nr.: {kat.artikelnummer}</span>}
                              {kat.kategorie && <span>{kat.kategorie}</span>}
                            </div>
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
                <p className="text-xs text-text-muted mt-1">
                  {selectedKatalog ? 'Katalog-Position ausgewählt' : 'Oder unten freie Bezeichnung eingeben'}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1 text-text">Bezeichnung *</label>
                <input
                  type="text"
                  value={formData.bezeichnung}
                  onChange={(e) => setFormData({ ...formData, bezeichnung: e.target.value })}
                  className="w-full px-3 py-2 border border-border rounded-button bg-surface text-text"
                  required={!formData.katalogPositionId}
                  placeholder="z.B. Floatglas 6mm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1 text-text">Menge</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.menge}
                  onChange={(e) => setFormData({ ...formData, menge: parseFloat(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-border rounded-button bg-surface text-text"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1 text-text">Einheit</label>
                <input
                  type="text"
                  value={formData.einheit}
                  onChange={(e) => setFormData({ ...formData, einheit: e.target.value })}
                  className="w-full px-3 py-2 border border-border rounded-button bg-surface text-text"
                  placeholder="Stück, m², lfm..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1 text-text">Einzelpreis netto (Cent)</label>
                <input
                  type="number"
                  value={formData.einzelpreisNetto}
                  onChange={(e) => setFormData({ ...formData, einzelpreisNetto: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-border rounded-button bg-surface text-text"
                />
                <p className="text-xs text-text-muted mt-1">= {formatCurrency(formData.einzelpreisNetto)}</p>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1 text-text">Beschreibung</label>
                <input
                  type="text"
                  value={formData.beschreibung}
                  onChange={(e) => setFormData({ ...formData, beschreibung: e.target.value })}
                  className="w-full px-3 py-2 border border-border rounded-button bg-surface text-text"
                  placeholder="Optional"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <button type="submit" className="px-4 py-2 bg-primary text-white rounded-button hover:bg-primary-dark transition">
                Hinzufügen
              </button>
              <button type="button" onClick={() => { setShowAdd(false); resetForm(); }} className="px-4 py-2 border border-border rounded-button hover:bg-surface-elevated transition">
                Abbrechen
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Material Liste */}
      {materialien.length === 0 ? (
        <p className="text-text-muted text-center py-8">Keine Materialien erfasst</p>
      ) : (
        <div className="space-y-2">
          {materialien.map(m => (
            <div key={m.id} className="flex items-center justify-between p-4 bg-surface-elevated rounded-button">
              <div className="flex items-center gap-4">
                <Package className="w-5 h-5 text-text-muted" />
                <div>
                  <p className="font-medium text-text">{m.bezeichnung}</p>
                  <p className="text-sm text-text-muted">
                    {m.menge} {m.einheit} x {formatCurrency(m.einzelpreisNetto)}
                    {m.beschreibung && ` - ${m.beschreibung}`}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-right">
                  <span className="font-semibold text-text">{formatCurrency(m.gesamtpreisNetto)}</span>
                  {m.gesamtkostenNetto != null && (
                    <span className="text-xs text-text-muted block">EK: {formatCurrency(m.gesamtkostenNetto)}</span>
                  )}
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={() => handleStatusChange(m, 'bestellt')}
                    className={`p-2 rounded transition ${m.bestellt ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-400 hover:bg-gray-200'}`}
                    title="Bestellt"
                  >
                    <Check className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleStatusChange(m, 'geliefert')}
                    className={`p-2 rounded transition ${m.geliefert ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400 hover:bg-gray-200'}`}
                    title="Geliefert"
                  >
                    <Truck className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleStatusChange(m, 'verbaut')}
                    className={`p-2 rounded transition ${m.verbaut ? 'bg-purple-100 text-purple-600' : 'bg-gray-100 text-gray-400 hover:bg-gray-200'}`}
                    title="Verbaut"
                  >
                    <Wrench className="w-4 h-4" />
                  </button>
                </div>
                <button
                  onClick={() => handleDelete(m.id)}
                  className="p-2 text-red-500 hover:bg-red-50 rounded transition"
                  title="Löschen"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
